import { useFormContext } from 'react-hook-form';

import { JOURNEY_TYPE, RAIL_EUROPE_SEARCH_CONDITION_TYPE } from '~constants/pointToPointTicket';
import { useDetermineMobile } from '@myrealtrip/device-detect';

import type { SearchPlaceInformation } from '~models/Transportation/PointToPointTicket';
import type {
  Direction,
  PointToPointTicketFormProps,
  SearchConditionPopupLocation,
} from '~types/pointToPointTicket';
import type { JourneyType, SearchConditionType } from '~types/queryParam';

import PlaceSearchSheet from './PlaceSearchSheet';
import PlaceSearchDropdown from './PlaceSearchDropdown';
import DatePickerSheet from './DatePickerSheet';
import DatePickerDropdown from './DatePickerDropdown';
import TravelerConditionSheet from './TravelerConditionSheet';
import TravelerDropdown from './TravelerDropdown';

interface Props {
  popupLocations: SearchConditionPopupLocation[];
  selectedJourney: JourneyType;
  openContent: SearchConditionType | null;
  showTimeOption?: boolean;
  closeContent: () => void;
}

type CommonDetailConditionProps = {
  isOpen: boolean;
  onClose: () => void;
};

type PlaceSearchProps = CommonDetailConditionProps & {
  direction: 'origin' | 'destination';
  popupLocation?: SearchConditionPopupLocation;
  onChangePlace: (direction: Direction, place: SearchPlaceInformation) => void;
};

type DatePickerProps = CommonDetailConditionProps & {
  isSingleTrip: boolean;
  departureDate: string;
  arrivalDate?: string;
  outboundDepartureTime?: string;
  inboundDepartureTime?: string;
  popupLocation?: SearchConditionPopupLocation;
  onApply: (params: {
    startDate: string;
    outboundStartTime?: string;
    endDate?: string;
    inboundStartTime?: string;
  }) => void;
};

type TravelerProps = CommonDetailConditionProps & {
  currentTravelers: number[];
  popupLocation?: SearchConditionPopupLocation;
  onApply: (ages: number[]) => void;
};

const SearchConditionContent = ({
  popupLocations,
  selectedJourney,
  openContent,
  showTimeOption,
  closeContent,
}: Props) => {
  const isMobile = useDetermineMobile();
  const { getValues, setValue, watch } = useFormContext<PointToPointTicketFormProps>();
  const ages = watch('ages');

  const isSingleTrip = selectedJourney === JOURNEY_TYPE.ONE_WAY;

  const handleApplyAges = (ages: number[], closeAfterApply: boolean = true) => {
    setValue('ages', ages);
    if (closeAfterApply) {
      closeContent();
    }
  };

  const handleApplyDateTime = ({
    startDate,
    endDate,
    outboundStartTime,
    inboundStartTime,
  }: {
    startDate: string;
    outboundStartTime?: string;
    endDate?: string;
    inboundStartTime?: string;
  }) => {
    setValue('departureDate', startDate);
    if (outboundStartTime) {
      setValue('outboundDepartureTime', outboundStartTime);
    }
    if (!isSingleTrip && endDate) {
      setValue('arrivalDate', endDate);
      setValue('inboundDepartureTime', inboundStartTime);
    }
    closeContent();
  };

  const handleChangePlace = (direction: Direction, place: SearchPlaceInformation) => {
    setValue(direction, place);
  };

  if (!openContent) return null;

  const componentConfig = {
    [RAIL_EUROPE_SEARCH_CONDITION_TYPE.ORIGIN]: isMobile ? PlaceSearchSheet : PlaceSearchDropdown,
    [RAIL_EUROPE_SEARCH_CONDITION_TYPE.DESTINATION]: isMobile
      ? PlaceSearchSheet
      : PlaceSearchDropdown,
    [RAIL_EUROPE_SEARCH_CONDITION_TYPE.DATE]: isMobile ? DatePickerSheet : DatePickerDropdown,
    [RAIL_EUROPE_SEARCH_CONDITION_TYPE.AGES]: isMobile ? TravelerConditionSheet : TravelerDropdown,
  } as const;

  const getComponentProps = () => {
    const commonProps = { isOpen: true, onClose: closeContent };

    if (
      openContent === RAIL_EUROPE_SEARCH_CONDITION_TYPE.ORIGIN ||
      openContent === RAIL_EUROPE_SEARCH_CONDITION_TYPE.DESTINATION
    ) {
      return {
        ...commonProps,
        direction: openContent as Direction,
        ...(!isMobile && {
          popupLocation: popupLocations.find((item) => item.type === openContent),
        }),
        onChangePlace: handleChangePlace,
      } as PlaceSearchProps;
    }

    if (openContent === RAIL_EUROPE_SEARCH_CONDITION_TYPE.DATE) {
      return {
        ...commonProps,
        isSingleTrip,
        departureDate: getValues('departureDate'),
        arrivalDate: getValues('arrivalDate'),
        showTimeOption,
        outboundDepartureTime: getValues('outboundDepartureTime'),
        inboundDepartureTime: getValues('inboundDepartureTime'),
        ...(!isMobile && {
          popupLocation: popupLocations.find((item) => item.type === openContent),
        }),
        onApply: handleApplyDateTime,
      } as DatePickerProps;
    }

    if (openContent === RAIL_EUROPE_SEARCH_CONDITION_TYPE.AGES) {
      return {
        ...commonProps,
        currentTravelers: isMobile ? ages : ages?.length > 0 ? ages : [],
        ...(!isMobile && {
          popupLocation: popupLocations.find((item) => item.type === openContent),
        }),
        onApply: handleApplyAges,
      } as TravelerProps;
    }

    return commonProps;
  };

  const Component = componentConfig[openContent];

  if (!Component) return null;

  return <Component {...(getComponentProps() as any)} />;
};

export default SearchConditionContent;
